import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import { useTeamContext } from '@/contexts/TeamContext'
import { useUserInit } from '@/contexts/UserInitContext'
import { useAuth } from '@clerk/clerk-react'
import { authTokenManager } from '@/utils/authTokenManager'
import type { UnifiedDomain } from '@shared/types'

interface DomainContextType {
  // Domain list
  domains: UnifiedDomain[]
  isLoadingDomains: boolean
  refetchDomains: () => void

  // Selected domain
  selectedDomain: UnifiedDomain | null
  selectedDomainName: string | null
  setSelectedDomain: (domain: UnifiedDomain | null) => void

  // Helpers
  hasDomains: boolean
  isNoDomainState: boolean
}

export const DomainContext = createContext<DomainContextType | undefined>(undefined)

const STORAGE_KEY_PREFIX = 'superschema_selected_domain_'

function getStorageKey(teamId: string | null | undefined): string {
  return `${STORAGE_KEY_PREFIX}${teamId || 'personal'}`
}

export function DomainProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth()
  const { isInitialized } = useUserInit()
  const { currentTeam } = useTeamContext()

  const teamId = currentTeam?.team?.id || null
  const isAuthReady = isLoaded && isSignedIn && authTokenManager.isReady() && isInitialized

  const [selectedDomain, setSelectedDomainState] = useState<UnifiedDomain | null>(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const prevTeamIdRef = useRef<string | null>(null)

  // Fetch domains for current team
  const {
    data: domainsResponse,
    isLoading: isLoadingDomains,
    refetch: refetchDomains
  } = useQuery({
    queryKey: ['unified-domains', teamId],
    queryFn: async () => {
      const response = await apiService.getUnifiedDomains()
      return response.data
    },
    enabled: isAuthReady && !!teamId,
    staleTime: 30000
  })

  const domains = useMemo(() => domainsResponse || [], [domainsResponse])

  // Set selected domain with localStorage persistence
  const setSelectedDomain = useCallback((domain: UnifiedDomain | null) => {
    setSelectedDomainState(domain)
    if (teamId) {
      const key = getStorageKey(teamId)
      if (domain) {
        localStorage.setItem(key, domain.domain)
      } else {
        localStorage.removeItem(key)
      }
    }
  }, [teamId])

  // Initialize or re-initialize selected domain when domains load or team changes
  useEffect(() => {
    if (isLoadingDomains || !teamId) return

    // Detect team change — reset before re-initializing
    const teamChanged = prevTeamIdRef.current !== null && prevTeamIdRef.current !== teamId
    prevTeamIdRef.current = teamId

    if (teamChanged) {
      // Team switched: clear stale selection, will re-init below
      setSelectedDomainState(null)
    }

    if (domains.length === 0) {
      setSelectedDomainState(null)
      setHasInitialized(true)
      return
    }

    // Try to restore from localStorage for this team
    const storedDomainName = localStorage.getItem(getStorageKey(teamId))
    if (storedDomainName) {
      const found = domains.find((d: UnifiedDomain) => d.domain === storedDomainName)
      if (found) {
        setSelectedDomainState(found)
        setHasInitialized(true)
        return
      }
    }

    // Default to first domain
    setSelectedDomainState(domains[0])
    setHasInitialized(true)
  }, [domains, isLoadingDomains, teamId])

  const contextValue = useMemo<DomainContextType>(() => ({
    domains,
    isLoadingDomains: isLoadingDomains || !hasInitialized,
    refetchDomains,
    selectedDomain,
    selectedDomainName: selectedDomain?.domain || null,
    setSelectedDomain,
    hasDomains: domains.length > 0,
    isNoDomainState: !isLoadingDomains && domains.length === 0 && hasInitialized
  }), [domains, isLoadingDomains, refetchDomains, selectedDomain, setSelectedDomain, hasInitialized])

  return (
    <DomainContext.Provider value={contextValue}>
      {children}
    </DomainContext.Provider>
  )
}
